<template>
    <div>
        <b-row v-if="isLoading">
            <b-col class="text-center py-5">
                <b-spinner class="mr-2" label="Loading" />
                <strong>Generating delivery log...</strong>
            </b-col>
        </b-row>
        <b-row v-else-if="pdfSource">
            <b-col>
                <b-embed type="embed" :src="pdfSource" style="width: 100%; height: 70vh;" />
            </b-col>
        </b-row>
        <b-row v-else>
            <b-col class="text-center py-5">
                <b-button variant="outline-info" @click="loadReport">
                    <i class="fa fa-redo fa-fw mr-1" />
                    Retry
                </b-button>
            </b-col>
        </b-row>
    </div>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator';
import axios, { CancelTokenSource } from 'axios';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

@Component({
    name: "DeliveryLogComponent"
})
export default class DeliveryLogComponent extends Vue {
    @Prop({ required: true }) orderId!: number;

    private pdfSource: string | null = null;
    private isLoading = false;
    private cancelSource: CancelTokenSource | null = null;

    mounted() {
        this.loadReport();
    }

    beforeDestroy() {
        // Abort an in-flight render (the modal is lazy, so closing it destroys us mid-request);
        // otherwise the resolved blob would be assigned to a dead component and never revoked.
        this.cancelSource?.cancel();
        this.releasePdf();
    }

    private releasePdf() {
        if (!this.pdfSource) return;
        URL.revokeObjectURL(this.pdfSource);
        this.pdfSource = null;
    }

    async loadReport() {
        if (this.isLoading) return;
        this.releasePdf();

        this.cancelSource = axios.CancelToken.source();
        try {
            this.isLoading = true;
            const response = await axios.get(`reports/deliveryLog/${this.orderId}`,
                { responseType: "blob", cancelToken: this.cancelSource.token });
            this.pdfSource = URL.createObjectURL(response.data);
        } catch (err) {
            if (!axios.isCancel(err)) this.$notification(NotificationOptions.error(await DeliveryLogComponent.readError(err)));
        } finally {
            this.isLoading = false;
            this.cancelSource = null;
        }
    }

    // responseType "blob" applies to error responses too, so the server's message arrives as a Blob
    // rather than a string and would otherwise surface to the user as "[object Blob]".
    private static async readError(err: any): Promise<any> {
        const data = err?.response?.data;
        if (data instanceof Blob) {
            try {
                return (await data.text()) || err;
            } catch {
                return err;
            }
        }
        return err;
    }
}
</script>
